import { useCallback } from "react";
import useActionDialog from "../../components/ActionDialog";
import {
    FormFieldErrors,
    hasErrors,
    useForm,
    validateMandatoryField,
    validateNumberField,
} from "../../utils/form";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { AddOutlined, CloseOutlined, DeleteOutlined } from "@mui/icons-material";
import { useUserInfo } from "../../utils/security";
import { isEmpty, set } from "lodash";
import NumberField from "../../components/NumberField";
import CurrencyField from "../../components/CurrencyField";
import { formatNum, parseNum } from "../../utils/numbers";
import Autocomplete from "../../components/Autocomplete";
import { wait } from "../../utils/mock";

const TransactionCounterpartyDialog = ({
    open,
    hide,
    onChange,
    data,
    disabled,
    title,
    idNumberLabel,
    existingLabel,
    nonExistingLabel,
}) => {
    const { userInfo } = useUserInfo();
    const showDialog = useActionDialog();
    const onSubmit = useCallback(
        (values) => {
            onChange?.(values);
            hide();
        },
        [hide, onChange]
    );
    const validate = useCallback(
        (values, errors) => {
            if (values.mode === "existing") {
                validateMandatoryField(values.id, "id", errors);
            }
            validateMandatoryField(values.name, "name", errors);
        },
        [userInfo]
    );

    const mapDataToFields = useCallback(
        ({ units, unitPrice, total, ...values }) => ({
            units: formatNum(units, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            unitPrice: formatNum(
                unitPrice,
                2,
                userInfo.decimalDelimiter,
                userInfo.thousandDelimiter
            ),
            total: formatNum(total, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...values,
        }),
        [userInfo]
    );
    const mapFieldsToData = useCallback(
        ({ units, unitPrice, total, ...values }) => ({
            units: parseNum(units, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            unitPrice: parseNum(unitPrice, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            total: parseNum(total, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...values,
        }),
        [userInfo]
    );
    const { values, handleChange, handleSubmit, setValues, errors } = useForm({
        data,
        onSubmit,
        validate,
        mapDataToFields,
        mapFieldsToData,
    });

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={hide}>
            <DialogTitle>{title || "Protistrana"}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={hide}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseOutlined />
            </IconButton>
            <DialogContent>
                <Box mb={2}>
                    <FormControl>
                        <RadioGroup
                            value={values.mode || "existing"}
                            onChange={(e, mode) => {
                                setValues({ mode }, true);
                            }}>
                            <FormControlLabel
                                value="existing"
                                control={<Radio />}
                                label="Vybrat uložený záznam"
                            />
                            <FormControlLabel
                                value="non-existing"
                                control={<Radio />}
                                label="Zadat ručně"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Box mb={2}>
                    {values.mode === "existing" && (
                        <Autocomplete
                            label={title || "Protistrana"}
                            placeholder="Zadejte název, IČ nebo DIČ"
                            value={
                                values.id
                                    ? { id: values.id, name: values.realName || values.name }
                                    : null
                            }
                            valueLabel={values.name || ""}
                            onChange={(value) => {
                                setValues({
                                    ...values,
                                    id: value?.id,
                                    name: value?.name,
                                    idNumber: value?.idNumber,
                                    taxIdNumber: value?.taxIdNumber,
                                    address: value?.address,
                                });
                            }}
                            disabled={disabled}
                            fetchOptions={async (input) => {
                                await wait(1400);
                                return [
                                    {
                                        id: "1",
                                        name: "Testovací firma",
                                        idNumber: "12345",
                                        taxIdNumber: "CZ12345",
                                        address: "Falešná ulice 123\nPraha",
                                    },
                                    {
                                        id: "2",
                                        name: "Alza.cz a.s.",
                                        idNumber: "12345",
                                        taxIdNumber: "CZ12345",
                                        address: "Falešná ulice 123\nPraha",
                                    },
                                ];
                            }}
                            getOptionLabel={(option) => option.name}
                            required
                            margin="normal"
                            fullWidth
                            error={hasErrors(errors, "id")}
                            helperText={<FormFieldErrors errors={errors} field="id" />}
                        />
                    )}
                </Box>
                <Box py={2}>
                    {(values.mode === "non-existing" || !isEmpty(values.id)) && (
                        <Fields
                            values={values}
                            errors={errors}
                            handleChange={handleChange}
                            disabled={disabled}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={hide} disabled={disabled}>
                    Storno
                </Button>
                <Button variant="contained" disabled={disabled} onClick={handleSubmit}>
                    Uložit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransactionCounterpartyDialog;

const Fields = ({ values, errors, handleChange, disabled }) => {
    return (
        <>
            {values.mode === "existing" && (
                <FormHelperText sx={{ mb: 2 }}>
                    Zkontrolujte prosím vyplněné údaje a případně je upravte. Zde provedené změny se
                    projeví pouze na této transakci.
                </FormHelperText>
            )}
            <TextField
                label="Název"
                name="name"
                value={values.name || ""}
                onChange={handleChange}
                disabled={disabled}
                required
                margin="normal"
                fullWidth
                error={hasErrors(errors, "name")}
                helperText={<FormFieldErrors errors={errors} field="name" />}
            />

            <Stack direction="row" spacing={2} sx={{ my: 2 }}>
                <TextField
                    label="IČ"
                    name="idNumber"
                    value={values.idNumber || ""}
                    onChange={handleChange}
                    margin="normal"
                    fullWidth
                    error={hasErrors(errors, "idNumber")}
                    helperText={<FormFieldErrors errors={errors} field="idNumber" />}
                />
                <TextField
                    label="DIČ"
                    name="taxIdNumber"
                    value={values.taxIdNumber || ""}
                    onChange={handleChange}
                    margin="normal"
                    fullWidth
                    error={hasErrors(errors, "taxIdNumber")}
                    helperText={<FormFieldErrors errors={errors} field="taxIdNumber" />}
                />
            </Stack>

            <TextField
                label="Adresa"
                name="address"
                value={values.address || ""}
                onChange={handleChange}
                margin="normal"
                fullWidth
                multiline
                rows={4}
                error={hasErrors(errors, "address")}
                helperText={<FormFieldErrors errors={errors} field="address" />}
            />
        </>
    );
};
